import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';

type ShareConfirmationModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemType: 'resource' | 'provider';
  itemName: string;
  onConfirm: () => void;
  onCancel: () => void;
};

/**
 * Confirmation modal that prompts users to share a resource or refer a provider
 * immediately after favoriting or adding to network.
 *
 * @param open - Controls modal visibility
 * @param onOpenChange - Callback when modal open state changes
 * @param itemType - Type of item being shared ('resource' or 'provider')
 * @param itemName - Name of the item for display purposes
 * @param onConfirm - Callback when user clicks "Yes, Share Now"
 * @param onCancel - Callback when user clicks "No, Not Now" or closes modal
 */
export function ShareConfirmationModal({
  open,
  onOpenChange,
  itemType,
  itemName,
  onConfirm,
  onCancel,
}: ShareConfirmationModalProps) {
  const title =
    itemType === 'resource'
      ? 'Share Resource with Client?'
      : 'Refer Provider to Client?';

  const message =
    itemType === 'resource'
      ? `Would you like to share "${itemName}" with a client now?`
      : `Would you like to refer ${itemName} to a client now?`;

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      onCancel();
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl">{title}</DialogTitle>
          <DialogDescription className="text-base text-gray-700 pt-4">
            {message}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onCancel}>
            No, Not Now
          </Button>
          <Button
            onClick={onConfirm}
            className="bg-[#026857] hover:bg-[#025246]"
          >
            Yes, Share Now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
